import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../_shared';

export { RATE_MODE_OPTIONS };

export const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'OTHER', label: 'Otro' },
] as const;

export const PAYMENT_PERIODICITY_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensual' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'SEMIANNUAL', label: 'Semestral' },
  { value: 'ANNUAL', label: 'Anual' },
] as const;

export const COMPENSATION_PLAN_OPTIONS = [
  { value: 'A', label: 'Plan A' },
  { value: 'B', label: 'Plan B' },
] as const;

export const SAVINGS_MODE_OPTIONS = [
  { value: 'goal_value', label: 'Prima ideal (cubre el 100%)' },
  { value: 'savings_capacity', label: 'Prima convenida (cobertura parcial)' },
] as const;

export const beneficiarySchema = z.object({
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  duration_years: z.coerce.number().int().positive('Debe ser > 0'),
  annual_tuition: z.coerce.number().nonnegative('Debe ser ≥ 0'),
});

export type Beneficiary = z.infer<typeof beneficiarySchema>;

const sharedFields = {
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  ...rateModeFields,
  compensation_plan: z.enum(['A', 'B']),
  payment_periodicity: z.enum(['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL']),
  death_insured_capital: z.coerce.number().nonnegative(),
  accidental_death_insured_capital: z.coerce.number().nonnegative(),
  itp_insured_capital: z.coerce.number().nonnegative(),
  serious_illness_insured_capital: z.coerce.number().nonnegative(),
  initial_contribution: z.coerce.number().nonnegative(),
  unscheduled_annual_contribution: z.coerce.number().nonnegative(),
};

export const insuredUniversitySchema = z.discriminatedUnion('savings_mode', [
  z.object({
    ...sharedFields,
    savings_mode: z.literal('goal_value'),
  }),
  z.object({
    ...sharedFields,
    savings_mode: z.literal('savings_capacity'),
    agreed_premium: z.coerce.number().positive('Debe ser > 0'),
  }),
]);

export type InsuredUniversityFormValues = z.infer<typeof insuredUniversitySchema>;

export const isFixedRate = (v: InsuredUniversityFormValues) => v.rate_mode === 'fixed';
export const isSteppedRate = (v: InsuredUniversityFormValues) => v.rate_mode === 'stepped';

export const insuredUniversityDefaults: InsuredUniversityFormValues = {
  savings_mode: 'goal_value',
  date_of_birth: '1990-01-01',
  gender: 'MALE',
  ...rateModeDefaults,
  projection_rate: 0.0331,
  payment_periodicity: 'MONTHLY',
  compensation_plan: 'A',
  death_insured_capital: 1000,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  serious_illness_insured_capital: 0,
  initial_contribution: 0,
  unscheduled_annual_contribution: 0,
};

export const beneficiaryDefaults: Beneficiary = {
  date_of_birth: '2009-06-01',
  duration_years: 4,
  annual_tuition: 30,
};
